import { graphqlClient } from '@/lib/graphql-client';
import { runWorkflow } from '@/lib/workflow-webhook';
import { buildGetToilRequestsQuery } from '../graphql/queries';
import {
  GetToilRequestsParams,
  ToilForEmployeeInput,
  ToilRequestsPagingResult,
  ToilStatus,
} from '../types/toil-manager.types';

/** Published webhook URLs of the TOIL manager workflows (blocks/workflows/toil). They save with the
 * service credential and check the caller's permission, department and the request's status
 * themselves; a refusal throws its translation key. */
const TOIL_REVIEW_WEBHOOK_URL = import.meta.env.VITE_TOIL_REVIEW_WEBHOOK_URL ?? '';
const TOIL_ARCHIVE_WEBHOOK_URL = import.meta.env.VITE_TOIL_ARCHIVE_WEBHOOK_URL ?? '';
const TOIL_DELETE_WEBHOOK_URL = import.meta.env.VITE_TOIL_DELETE_WEBHOOK_URL ?? '';
const TOIL_REQUEST_WEBHOOK_URL = import.meta.env.VITE_TOIL_REQUEST_WEBHOOK_URL ?? '';

export const getToilRequests = async (
  params: GetToilRequestsParams
): Promise<{ ToilRequests: ToilRequestsPagingResult }> => {
  const { pageNo, pageSize, filter = {}, order } = params;
  return graphqlClient.query({
    query: buildGetToilRequestsQuery(filter, pageNo, pageSize, order),
  });
};

/** Approves or declines a Pending request (TOIL_REVIEW); a decline needs a note. */
export const reviewToilRequest = (params: { itemId: string; status: ToilStatus; note?: string }) =>
  runWorkflow(
    TOIL_REVIEW_WEBHOOK_URL,
    'VITE_TOIL_REVIEW_WEBHOOK_URL',
    { ...params, note: params.note ?? '' },
    'TOIL_SAVE_FAILED'
  );

/** Archives a reviewed request (TOIL_ARCHIVE). */
export const archiveToilRequest = (itemId: string) =>
  runWorkflow(
    TOIL_ARCHIVE_WEBHOOK_URL,
    'VITE_TOIL_ARCHIVE_WEBHOOK_URL',
    { itemId },
    'TOIL_SAVE_FAILED'
  );

/** Deletes a request (TOIL_DELETE, soft delete). */
export const deleteToilRequest = (itemId: string) =>
  runWorkflow(
    TOIL_DELETE_WEBHOOK_URL,
    'VITE_TOIL_DELETE_WEBHOOK_URL',
    { itemId },
    'TOIL_SAVE_FAILED'
  );

/**
 * Files TOIL for an employee (TOIL_REQUEST with `userId`): needs `hrm::toil::request-for-others` and,
 * for a head of department, one of their departments. Past days are allowed; the other TOIL rules hold.
 */
export const requestToilForEmployee = (input: ToilForEmployeeInput) =>
  runWorkflow(
    TOIL_REQUEST_WEBHOOK_URL,
    'VITE_TOIL_REQUEST_WEBHOOK_URL',
    { ...input },
    'TOIL_SAVE_FAILED'
  );
